/**
 * Schlanker Layout-Wrapper für /partner/*. Eigene Tab-Navigation,
 * unabhängig vom großen StudentLayout, damit der Affiliate-Bereich
 * isoliert getestet/iteriert werden kann.
 */
import { ReactNode } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Link2, Banknote, ArrowLeft, Loader2, Lock, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useMyAffiliate } from "@/hooks/useMyAffiliate";
import StudentLayout from "@/components/student/StudentLayout";

interface PartnerLayoutProps {
  children: ReactNode;
  title: string;
}

const tabs = [
  { to: "/partner", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/partner/kunden", label: "Kunden", icon: Users, end: false },
  { to: "/partner/links", label: "Meine Links", icon: Link2, end: false },
  { to: "/partner/auszahlungen", label: "Auszahlungen", icon: Banknote, end: false },
];

const PartnerLayout = ({ children, title }: PartnerLayoutProps) => {
  const navigate = useNavigate();
  const { data: user, isLoading: userLoading } = useCurrentUser();
  const { data: affiliate, isLoading: affLoading } = useMyAffiliate(user?.id);

  if (userLoading || affLoading) {
    return (
      <StudentLayout>
        <div className="flex min-h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </StudentLayout>
    );
  }

  if (!user) {
    return (
      <StudentLayout>
        <div className="container mx-auto max-w-xl py-16 text-center">
          <h1 className="mb-4 text-3xl font-bold">Partnerbereich</h1>
          <p className="mb-6 text-muted-foreground">Bitte melde dich an.</p>
          <Button onClick={() => navigate("/login?redirect=/partner")}>Zum Login</Button>
        </div>
      </StudentLayout>
    );
  }

  if (!affiliate || affiliate.status !== "active") {
    return (
      <StudentLayout>
        <div className="container mx-auto max-w-xl py-16 text-center">
          <Lock className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
          <h1 className="mb-2 text-2xl font-bold">Partnerbereich noch nicht freigeschaltet</h1>
          <p className="mb-6 text-muted-foreground">
            {!affiliate
              ? "Du bist noch nicht als Partner registriert."
              : affiliate.status === "pending"
                ? "Deine Bewerbung wird gerade geprüft."
                : "Dein Account ist aktuell nicht aktiv."}
          </p>
          <Button onClick={() => navigate("/partner/werden")}>
            {affiliate ? "Status ansehen" : "Jetzt bewerben"}
          </Button>
        </div>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout>
      <div className="container mx-auto max-w-6xl px-4 py-8">
        <button
          type="button"
          onClick={() => navigate("/mein-bereich/partner")}
          className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Zurück zum Partnerprogramm
        </button>
        <div className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
          <h1 className="text-3xl font-bold">{title}</h1>
          <div className="text-sm text-muted-foreground">
            Dein Code: <span className="font-mono font-semibold text-foreground">{affiliate.affiliate_code}</span>
          </div>
        </div>

        <nav className="mb-6 flex flex-wrap gap-2 border-b">
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              className={({ isActive }) =>
                `inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm transition ${
                  isActive
                    ? "border-primary font-medium text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`
              }
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </NavLink>
          ))}
        </nav>

        {children}
      </div>
    </StudentLayout>
  );
};

export default PartnerLayout;
